import React, { useEffect, useState } from "react"
import {
    Backdrop, CircularProgress, Container, Grid, Snackbar, Switch, Tooltip,
} from "@material-ui/core"
import { Alert } from "@material-ui/lab"
import backdropStyles from '../AgentSkillsManagement/styles.module.css'
import formStyles from '../TeamsManagement/styles.module.css'
import mainStyles from '../WorkerManagementComponent/WorkerManagementComponent.module.css'
import styles from './styles.module.css'
import { BlueBtn, GreyInverseBtn } from "../Styled/StyledButtons"
import { RobocallBlocksActions } from "../../actions/RobocallBlocksActions"
import { ISettingsResponse } from "../../models/RobocallBlocks/BlockedNumber"

/**
 * Robocall blocker settings.
 *
 * The three switches are the reason this screen exists. Until now the only way to turn the
 * blocker off was editing a JSON blob in the `system` table, which is not something anybody
 * should be doing at 3am while calls are being rejected.
 *
 * Two behaviours worth knowing, both handled server-side:
 *   - saving invalidates the 60-minute config cache, so a change takes effect on the next
 *     call rather than within the hour;
 *   - turning the blocker off drops every cached block key synchronously, so the kill
 *     switch does not wait for the 5-minute reconcile job.
 */
const Settings = () => {
    const [data, setData] = useState<ISettingsResponse | null>(null)
    const [values, setValues] = useState<{ [k: string]: any }>({})
    const [reasonIds, setReasonIds] = useState<{ [g: string]: number[] }>({})
    const [comment, setComment] = useState('')
    const [backdrop, setBackdrop] = useState(true)
    const [saving, setSaving] = useState(false)
    const [alert, setAlert] = useState<{ severity: 'error' | 'success' | 'warning', text: string } | null>(null)
    const [fieldErrors, setFieldErrors] = useState<{ [k: string]: string }>({})

    const load = () => {
        RobocallBlocksActions.getSettings()
            .then((res: ISettingsResponse) => {
                setData(res)
                setValues({ ...res.values })
                setReasonIds({ ...res.reason_ids })
                setBackdrop(false)
            })
            .catch((error) => {
                setBackdrop(false)
                setAlert({ severity: 'error', text: describe(error) })
            })
    }

    useEffect(load, [])

    const describe = (error: any): string => {
        const d = error?.response?.data
        if (d?.messages) {
            return Object.keys(d.messages).map((k) => d.messages[k]).join(' ')
        }
        return d?.error || error?.message || 'Request failed'
    }

    // Only send what the reviewer actually moved, so a stale field in this form cannot
    // quietly overwrite something changed elsewhere in the meantime.
    const changed = (): { payload: any, count: number } => {
        const payload: any = {}
        let count = 0

        if (data === null) {
            return { payload, count }
        }

        Object.keys(data.schema.fields).forEach((key) => {
            const before = data.values[key]
            const now = values[key]
            if (Number(before) !== Number(now)) {
                payload[key] = typeof now === 'boolean' ? (now ? 1 : 0) : now
                count++
            }
        })

        const reasonPayload: any = {}
        Object.keys(data.schema.reason_groups).forEach((group) => {
            const before = [...(data.reason_ids[group] || [])].sort().join(',')
            const now = [...(reasonIds[group] || [])].sort().join(',')
            if (before !== now) {
                reasonPayload[group] = reasonIds[group] || []
                count++
            }
        })
        if (Object.keys(reasonPayload).length > 0) {
            payload.reason_ids = reasonPayload
        }

        return { payload, count }
    }

    const save = () => {
        const { payload, count } = changed()
        if (count === 0) {
            setAlert({ severity: 'warning', text: 'Nothing changed.' })
            return
        }
        if (comment.trim().length < 4) {
            setAlert({ severity: 'error', text: 'A comment is required.' })
            return
        }

        setSaving(true)
        setFieldErrors({})
        RobocallBlocksActions.updateSettings(payload, comment.trim())
            .then((res: any) => {
                setSaving(false)
                setComment('')
                setAlert({
                    severity: 'success',
                    text: res.effect ? `Saved — ${res.effect}.` : 'Saved.',
                })
                load()
            })
            .catch((error) => {
                setSaving(false)
                const messages = error?.response?.data?.messages
                if (messages) {
                    setFieldErrors(messages)
                }
                setAlert({ severity: 'error', text: describe(error) })
            })
    }

    const setValue = (key: string, value: any) => setValues({ ...values, [key]: value })

    const toggleReason = (group: string, id: number) => {
        const current = reasonIds[group] || []
        setReasonIds({
            ...reasonIds,
            [group]: current.indexOf(id) === -1
                ? [...current, id]
                : current.filter((x) => x !== id),
        })
    }

    const { count: changeCount } = changed()

    const stateClass = (state: string) => {
        if (state === 'live') { return styles.StatusActive }
        if (state === 'dry_run') { return styles.StatusCandidate }
        return styles.StatusExpired
    }

    return (
        <>
            <Container className={mainStyles.mainContainer} maxWidth={false}>
                {data !== null ?
                    <Grid container spacing={1}>

                        {/* ---------------------------------------------- state banner */}
                        <Grid item xs={12}>
                            <div className={styles.Panel}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={3}>
                                        <span className={`${styles.Status} ${stateClass(data.effective_state.state)}`}>
                                            {data.effective_state.label.toUpperCase()}
                                        </span>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <div className={styles.Evidence}>{data.effective_state.detail}</div>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <GreyInverseBtn fullWidth={true} href="/robocall-blocks">
                                            Back to the queue
                                        </GreyInverseBtn>
                                    </Grid>
                                </Grid>
                            </div>
                        </Grid>

                        {/* ---------------------------------------------- problems */}
                        {data.problems.length > 0 ?
                            <Grid item xs={12}>
                                <div className={styles.Panel}>
                                    {data.problems.map((p, i) => (
                                        <Alert key={i} severity={p.severity === 'error' ? 'error' : 'warning'}>
                                            <b>{p.group}</b>: {p.message}
                                        </Alert>
                                    ))}
                                </div>
                            </Grid>
                            : null}

                        {/* ---------------------------------------------- fields */}
                        {Object.keys(data.schema.groups).map((group) => {
                            const fields = Object.keys(data.schema.fields)
                                .filter((k) => data.schema.fields[k].group === group)
                            if (fields.length === 0) {
                                return null
                            }
                            return (
                                <Grid item xs={12} key={group}>
                                    <div className={styles.Panel}>
                                        <h4>{data.schema.groups[group]}</h4>
                                        <Grid container spacing={2}>
                                            {fields.map((key) => {
                                                const field = data.schema.fields[key]
                                                const err = fieldErrors[key]
                                                return (
                                                    <Grid item xs={12} md={group === 'switches' ? 4 : 3} key={key}>
                                                        {field.type === 'bool' ?
                                                            <label className={styles.Evidence}>
                                                                <Switch
                                                                    checked={!!values[key]}
                                                                    color="primary"
                                                                    onChange={(e) => setValue(key, e.target.checked)}
                                                                />
                                                                <b>{field.label}</b>
                                                            </label>
                                                            :
                                                            <>
                                                                <label className={formStyles.FormLabel}>
                                                                    {field.label}
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    min={field.min}
                                                                    max={field.max}
                                                                    className={`${err ? formStyles.FormInvalid : ''} ${formStyles.FormControl}`}
                                                                    value={values[key] as any}
                                                                    onChange={(e) => setValue(key, e.target.value === ''
                                                                        ? ''
                                                                        : parseInt(e.target.value, 10))}
                                                                />
                                                            </>
                                                        }
                                                        <div className={styles.Hint}>{field.help}</div>
                                                        {err ? <div className={styles.EvidenceWarn}>{err}</div> : null}
                                                    </Grid>
                                                )
                                            })}
                                        </Grid>
                                    </div>
                                </Grid>
                            )
                        })}

                        {/* ---------------------------------------------- reason map */}
                        <Grid item xs={12}>
                            <div className={styles.Panel}>
                                <h4>Call classifications</h4>
                                <div className={styles.Hint}>
                                    Which agent classifications count as spam, as a real lead, or
                                    are ignored. Names come from the live dictionary, so an id
                                    that no longer exists cannot be picked by accident.
                                </div>
                                <Grid container spacing={2}>
                                    {Object.keys(data.schema.reason_groups).map((group) => {
                                        const meta = data.schema.reason_groups[group]
                                        const selected = reasonIds[group] || []
                                        const err = fieldErrors['reason_ids.' + group]
                                        return (
                                            <Grid item xs={12} md={3} key={group}>
                                                <label className={formStyles.FormLabel}>{meta.label}</label>
                                                <div className={styles.Hint}>{meta.help}</div>
                                                {err ? <div className={styles.EvidenceWarn}>{err}</div> : null}
                                                <div>
                                                    {data.reasons.map((reason) => (
                                                        <label
                                                            key={reason.id}
                                                            className={styles.Evidence}
                                                            style={{ display: 'block' }}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={selected.indexOf(reason.id) !== -1}
                                                                onChange={() => toggleReason(group, reason.id)}
                                                            />
                                                            &nbsp;
                                                            <Tooltip title={`id ${reason.id}`}>
                                                                <span className={reason.active ? undefined : styles.EvidenceMuted}>
                                                                    {reason.reason}
                                                                    {reason.active ? '' : ' (inactive)'}
                                                                </span>
                                                            </Tooltip>
                                                        </label>
                                                    ))}
                                                </div>
                                            </Grid>
                                        )
                                    })}
                                </Grid>
                            </div>
                        </Grid>

                        {/* ---------------------------------------------- save */}
                        <Grid item xs={12}>
                            <div className={styles.Panel}>
                                <Grid container spacing={2} alignItems="flex-end">
                                    <Grid item xs={12} md={7}>
                                        <label className={formStyles.FormLabel}>Comment (required)</label>
                                        <input
                                            placeholder="Why are you changing this?"
                                            className={formStyles.FormControl}
                                            value={comment}
                                            onChange={(e) => setComment(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <div className={styles.Evidence}>
                                            {changeCount} change{changeCount === 1 ? '' : 's'}
                                        </div>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <BlueBtn
                                            fullWidth={true}
                                            disabled={saving || changeCount === 0 || comment.trim().length < 4}
                                            onClick={save}
                                        >
                                            {saving ? 'Saving…' : 'Save settings'}
                                        </BlueBtn>
                                    </Grid>
                                </Grid>
                                <div className={styles.Hint}>
                                    Takes effect on the next call — the config cache is invalidated
                                    on save. Turning the blocker off also drops every cached block
                                    key immediately rather than waiting for the reconcile job.
                                </div>
                            </div>
                        </Grid>

                        {/* ---------------------------------------------- history */}
                        <Grid item xs={12}>
                            <div className={styles.Panel}>
                                <h4>Recent changes</h4>
                                {data.history.length === 0 ?
                                    <div className={styles.Empty}>No changes recorded yet.</div>
                                    :
                                    data.history.map((row) => (
                                        <div className={styles.HistoryRow} key={row.id}>
                                            <span className={styles.HistoryAction}>{row.created_at}</span>
                                            {' — '}
                                            {row.user?.email || (row.user_id ? `user ${row.user_id}` : 'system')}
                                            {' — '}
                                            {Object.keys(row.changes || {}).map((k) =>
                                                `${k}: ${JSON.stringify(row.changes[k].from)} → ${JSON.stringify(row.changes[k].to)}`
                                            ).join(' · ')}
                                            <div className={styles.EvidenceMuted}>{row.comment}</div>
                                        </div>
                                    ))
                                }
                            </div>
                        </Grid>
                    </Grid>
                    : <></>}
            </Container>

            <Snackbar
                open={alert !== null}
                autoHideDuration={6000}
                onClose={() => setAlert(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert onClose={() => setAlert(null)} severity={alert?.severity || 'error'} variant="filled">
                    <div className={backdropStyles.AlertError}>{alert?.text}</div>
                </Alert>
            </Snackbar>

            <Backdrop className={backdropStyles.Backdrop} open={backdrop}>
                <CircularProgress className={backdropStyles.BackdropProgress} />&nbsp;&nbsp;
                <p className={backdropStyles.BackdropProgress}>Loading...</p>
            </Backdrop>
        </>
    )
}

export default Settings
